import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Icon from "components/ShellV2/Icon";

function medalClass(idx) {
  if (idx === 0) return "rec-rank-medal rec-rank-1";
  if (idx === 1) return "rec-rank-medal rec-rank-2";
  if (idx === 2) return "rec-rank-medal rec-rank-3";
  return "rec-rank-medal rec-rank-n";
}

function formatValue(v, unit) {
  if (v == null) return "—";
  return `${Number(v).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}${unit ? ` ${unit}` : ""}`;
}

function formatDelta(v) {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Number(v);
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

export default function RankingList({
  members,
  scoreField,
  secondaryField,
  secondaryUnit,
  deltaField,
  unit,
  limit,
  footerLink,
  footerLabel,
  memberLinkBase,
}) {
  const { t } = useTranslation(["dashboard", "common"]);
  const list = Array.isArray(members) ? [...members] : [];
  list.sort((a, b) => (Number(b[scoreField]) || 0) - (Number(a[scoreField]) || 0));
  const top = list.slice(0, limit);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[15px] font-bold text-ink flex items-center gap-2">
          <Icon name="emoji_events" style={{ color: "var(--warm)" }} />
          {t("ranking.title", { defaultValue: "Top Membros" })}
        </div>
        <span className="rec-chip rec-chip-gray">
          {t("kpi.savings", { defaultValue: "Poupança" })}
        </span>
      </div>
      {top.length === 0 ? (
        <div className="text-ink-3 text-sm py-4">
          {t("ranking.empty", { defaultValue: "Sem dados para o período selecionado" })}
        </div>
      ) : (
        top.map((m, i) => {
          const name = m.name || m.member_name || (m.member && m.member.name) || "—";
          const id = m.id || m.member_id || `${name}-${i}`;
          const delta = deltaField ? formatDelta(m[deltaField]) : null;
          const deltaNum = deltaField ? Number(m[deltaField]) : null;
          let deltaColor = null;
          if (deltaNum != null && Number.isFinite(deltaNum)) {
            deltaColor = deltaNum >= 0 ? "var(--success)" : "var(--danger)";
          }
          const secondaryValue = secondaryField ? m[secondaryField] : null;
          const memberId = m.id || m.member_id;
          const clickable = Boolean(memberLinkBase && memberId);
          const rowClass = `rec-rank-row${
            clickable
              ? " no-underline text-inherit cursor-pointer transition-colors hover:bg-[var(--surface-2)]"
              : ""
          }`;
          const inner = (
            <>
              <span className={medalClass(i)}>{i + 1}</span>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-ink truncate">{name}</div>
                {secondaryValue != null && (
                  <div className="text-[11.5px] text-ink-3">
                    {formatValue(secondaryValue, secondaryUnit)}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[15px] font-bold text-ink tabular-nums">
                  {formatValue(m[scoreField], unit)}
                </div>
                {delta && (
                  <div
                    className="text-[11px] font-semibold tabular-nums"
                    style={{ color: deltaColor }}
                  >
                    {delta}{" "}
                    <span className="text-ink-3 font-normal">
                      {t("ranking.vsPrev", { defaultValue: "vs. mês anterior" })}
                    </span>
                  </div>
                )}
              </div>
            </>
          );
          return clickable ? (
            <Link key={id} to={`${memberLinkBase}/${memberId}`} className={rowClass}>
              {inner}
            </Link>
          ) : (
            <div key={id} className={rowClass}>
              {inner}
            </div>
          );
        })
      )}
      {footerLink && (
        <div className="mt-auto pt-3">
          <Link to={footerLink} className="rec-btn rec-btn-ghost rec-btn-sm w-full no-underline">
            {footerLabel || t("ranking.viewAll", { defaultValue: "Ver todos os membros" })}
            <Icon name="arrow_forward" />
          </Link>
        </div>
      )}
    </div>
  );
}

RankingList.propTypes = {
  members: PropTypes.arrayOf(PropTypes.shape({})),
  scoreField: PropTypes.string,
  secondaryField: PropTypes.string,
  secondaryUnit: PropTypes.string,
  deltaField: PropTypes.string,
  unit: PropTypes.string,
  limit: PropTypes.number,
  footerLink: PropTypes.string,
  footerLabel: PropTypes.string,
  memberLinkBase: PropTypes.string,
};

RankingList.defaultProps = {
  members: [],
  scoreField: "savings",
  secondaryField: null,
  secondaryUnit: null,
  deltaField: null,
  unit: "€",
  limit: 4,
  footerLink: null,
  footerLabel: null,
  memberLinkBase: null,
};
